import {
  EXPENSE_PAYMENT_MODES,
  type ExpenseDepartment,
  type ExpensePaymentMode,
} from '@kortex/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { PageHeader, Tabs } from '../components/Layout';
import {
  BarList,
  Empty,
  ErrorState,
  Kpi,
  Loading,
  Modal,
  Select,
  StatusChip,
  TextInput,
  useToast,
} from '../components/ui';
import { ApiError, api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { date, daysAgo, money, today } from '../lib/format';

/* ---------------- shared types ---------------- */

interface ExpenseCategory {
  id: string;
  name: string;
  description: string | null;
  department: ExpenseDepartment;
  isActive: boolean;
  sortOrder: number;
}

/** Which daily report a category's spending is deducted in. */
const DEPARTMENT_OPTIONS: Array<[ExpenseDepartment, string]> = [
  ['FNB', 'F&B'],
  ['FRONT_DESK', 'Front desk'],
  ['SHARED', 'Shared (overall report only)'],
];
const DEPARTMENT_HINT =
  'Decides which daily report deducts this spending. Shared costs belong to the whole property.';

function DepartmentSelect({
  value,
  onChange,
}: {
  value: ExpenseDepartment;
  onChange: (value: ExpenseDepartment) => void;
}) {
  return (
    <Select
      label="Reported under"
      hint={DEPARTMENT_HINT}
      value={value}
      onChange={(e) => onChange(e.target.value as ExpenseDepartment)}
    >
      {DEPARTMENT_OPTIONS.map(([id, label]) => (
        <option key={id} value={id}>
          {label}
        </option>
      ))}
    </Select>
  );
}

interface Expense {
  id: string;
  number: string;
  categoryId: string;
  category: string | null;
  amount: number;
  paymentMode: ExpensePaymentMode;
  expenseDate: string;
  vendorName: string | null;
  referenceNumber: string | null;
  description: string;
  notes: string | null;
  status: 'POSTED' | 'VOID';
  voidReason: string | null;
  createdById: string;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
}

interface CashSummary {
  date: string;
  openingCash: number;
  cashSales: number;
  cashExpenses: number;
  availableCash: number;
}

const PAYMENT_MODE_LABELS: Record<ExpensePaymentMode, string> = {
  CASH: 'Cash',
  CARD: 'Card',
  UPI: 'UPI',
  BANK_TRANSFER: 'Bank transfer',
  OTHER: 'Other',
};

/* ---------------- record expense (also used from the POS quick action) ---------------- */

export function RecordExpenseModal({
  onClose,
  onRecorded,
}: {
  onClose: () => void;
  onRecorded?: (expense: Expense) => void;
}) {
  const queryClient = useQueryClient();
  const { push, fail } = useToast();
  const categories = useQuery({
    queryKey: ['expense-categories', 'active'],
    queryFn: () => api.get<ExpenseCategory[]>('/api/expense-categories?activeOnly=true'),
  });
  const cash = useQuery({
    queryKey: ['expense-cash-summary'],
    queryFn: () => api.get<CashSummary>('/api/expenses/cash-summary'),
  });

  const [categoryId, setCategoryId] = useState('');
  const [amount, setAmount] = useState('');
  const [paymentMode, setPaymentMode] = useState<ExpensePaymentMode>('CASH');
  const [vendorName, setVendorName] = useState('');
  const [referenceNumber, setReferenceNumber] = useState('');
  const [description, setDescription] = useState('');
  const [notes, setNotes] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [editingFloat, setEditingFloat] = useState(false);
  const [floatInput, setFloatInput] = useState('');

  const setFloat = useMutation({
    mutationFn: () => api.post<CashSummary>('/api/expenses/cash-drawer', { amount: Number(floatInput) || 0 }),
    onSuccess: (summary) => {
      queryClient.setQueryData(['expense-cash-summary'], summary);
      setEditingFloat(false);
    },
    onError: fail,
  });

  const amountValue = Number(amount) || 0;
  const isCash = paymentMode === 'CASH';
  const remaining = cash.data ? cash.data.availableCash - amountValue : null;

  const record = useMutation({
    mutationFn: () =>
      api.post<Expense>('/api/expenses', {
        categoryId,
        amount: amountValue,
        paymentMode,
        expenseDate: today(),
        vendorName: vendorName.trim() || undefined,
        referenceNumber: referenceNumber.trim() || undefined,
        description: description.trim(),
        notes: notes.trim() || undefined,
      }),
    onSuccess: (expense) => {
      const remainingCash = isCash ? cash.data!.availableCash - expense.amount : null;
      push(
        remainingCash !== null
          ? `${money(expense.amount)} recorded. Cash remaining: ${money(remainingCash)}.`
          : `${money(expense.amount)} expense recorded.`,
        'ok',
      );
      void queryClient.invalidateQueries({ queryKey: ['expenses'] });
      void queryClient.invalidateQueries({ queryKey: ['expense-cash-summary'] });
      void queryClient.invalidateQueries({ queryKey: ['expense-daily-summary'] });
      onRecorded?.(expense);
      onClose();
    },
    onError: (error) => {
      setFormError(error instanceof ApiError ? error.message : 'Could not record the expense.');
      fail(error);
    },
  });

  const canSubmit =
    Boolean(categoryId) && amountValue > 0 && Boolean(description.trim()) && !record.isPending;

  return (
    <Modal
      title="Record expense"
      size="narrow"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn primary"
            disabled={!canSubmit || (isCash && remaining !== null && remaining < 0)}
            onClick={() => record.mutate()}
          >
            {record.isPending ? 'Recording…' : 'Record expense'}
          </button>
        </>
      }
    >
      {categories.isLoading ? (
        <Loading />
      ) : (
        <div className="col gap-14">
          {formError ? <div className="form-error">{formError}</div> : null}

          {categories.data && categories.data.length > 0 ? (
            <div className="row gap-6" style={{ flexWrap: 'wrap' }}>
              {categories.data.slice(0, 5).map((category) => (
                <button
                  key={category.id}
                  type="button"
                  className={`btn sm${categoryId === category.id ? ' primary' : ''}`}
                  onClick={() => setCategoryId(category.id)}
                >
                  {category.name}
                </button>
              ))}
            </div>
          ) : null}

          <Select
            label="Category *"
            required
            value={categoryId}
            onChange={(e) => setCategoryId(e.target.value)}
          >
            <option value="">Choose a category…</option>
            {(categories.data ?? []).map((category) => (
              <option key={category.id} value={category.id}>
                {category.name}
              </option>
            ))}
          </Select>

          <div className="form-grid">
            <TextInput
              label="Amount *"
              type="number"
              min="0.01"
              step="0.01"
              required
              autoFocus
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
            />
            <Select
              label="Payment method *"
              required
              value={paymentMode}
              onChange={(e) => setPaymentMode(e.target.value as ExpensePaymentMode)}
            >
              {EXPENSE_PAYMENT_MODES.map((mode) => (
                <option key={mode} value={mode}>
                  {PAYMENT_MODE_LABELS[mode]}
                </option>
              ))}
            </Select>
          </div>

          {isCash ? (
            <div className="card" style={{ padding: 12, background: 'var(--surface-2, #f7f7f7)' }}>
              <div className="row between small">
                <span className="dim">Opening cash today</span>
                {editingFloat ? (
                  <span className="row gap-4">
                    <input
                      className="input"
                      type="number"
                      min="0"
                      step="0.01"
                      value={floatInput}
                      onChange={(e) => setFloatInput(e.target.value)}
                      style={{ width: 90 }}
                      autoFocus
                    />
                    <button
                      className="btn sm primary"
                      disabled={setFloat.isPending}
                      onClick={() => setFloat.mutate()}
                    >
                      Save
                    </button>
                    <button className="btn sm" onClick={() => setEditingFloat(false)}>
                      Cancel
                    </button>
                  </span>
                ) : (
                  <span className="row gap-6">
                    <span className="b num">{cash.isLoading ? '…' : money(cash.data?.openingCash ?? 0)}</span>
                    <button
                      className="btn sm"
                      onClick={() => {
                        setFloatInput(String(cash.data?.openingCash ?? 0));
                        setEditingFloat(true);
                      }}
                    >
                      Edit
                    </button>
                  </span>
                )}
              </div>
              <div className="row between small" style={{ marginTop: 4 }}>
                <span className="dim">Available cash today</span>
                <span className="b num">{cash.isLoading ? '…' : money(cash.data?.availableCash ?? 0)}</span>
              </div>
              <div className="row between small" style={{ marginTop: 4 }}>
                <span className="dim">After this expense</span>
                <span
                  className="b num"
                  style={remaining !== null && remaining < 0 ? { color: 'var(--danger)' } : undefined}
                >
                  {remaining !== null ? money(remaining) : '—'}
                </span>
              </div>
              {remaining !== null && remaining < 0 ? (
                <div className="field-error" style={{ marginTop: 6 }}>
                  Insufficient cash available in the drawer - set today&apos;s opening cash above if
                  there is float in the cash drawer that hasn&apos;t been recorded yet.
                </div>
              ) : null}
            </div>
          ) : null}

          <TextInput
            label="Description *"
            required
            placeholder="What was this for?"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />

          <div className="form-grid">
            <TextInput
              label="Vendor / paid to"
              value={vendorName}
              onChange={(e) => setVendorName(e.target.value)}
            />
            <TextInput
              label="Reference number"
              value={referenceNumber}
              onChange={(e) => setReferenceNumber(e.target.value)}
            />
          </div>

          <TextInput
            label="Notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </div>
      )}
    </Modal>
  );
}

/* ---------------- expense list ---------------- */

function ExpenseListSection() {
  const queryClient = useQueryClient();
  const { can } = useAuth();
  const { push, fail } = useToast();
  const [from, setFrom] = useState(today());
  const [to, setTo] = useState(today());
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const [offset, setOffset] = useState(0);
  const [recording, setRecording] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);
  const [voiding, setVoiding] = useState(false);
  const [voidReason, setVoidReason] = useState('');
  const limit = 25;

  const params = new URLSearchParams({
    from,
    to,
    limit: String(limit),
    offset: String(offset),
    ...(status ? { status } : {}),
    ...(q ? { q } : {}),
  });

  const expenses = useQuery({
    queryKey: ['expenses', from, to, status, q, offset],
    queryFn: () => api.get<{ total: number; items: Expense[] }>(`/api/expenses?${params.toString()}`),
  });

  const detail = useQuery({
    queryKey: ['expense', openId],
    queryFn: () => api.get<Expense>(`/api/expenses/${openId}`),
    enabled: Boolean(openId),
  });

  const voidExpense = useMutation({
    mutationFn: () => api.post(`/api/expenses/${openId}/void`, { reason: voidReason }),
    onSuccess: () => {
      push('Expense voided', 'ok');
      setVoiding(false);
      setVoidReason('');
      setOpenId(null);
      void queryClient.invalidateQueries({ queryKey: ['expenses'] });
      void queryClient.invalidateQueries({ queryKey: ['expense-cash-summary'] });
    },
    onError: fail,
  });

  if (expenses.isLoading) return <Loading label="Loading expenses…" />;
  if (expenses.error) return <ErrorState error={expenses.error} onRetry={() => void expenses.refetch()} />;

  const rows = expenses.data?.items ?? [];
  const total = expenses.data?.total ?? 0;

  return (
    <div className="stack">
      <div className="card">
        <div className="card-head" style={{ flexWrap: 'wrap', gap: 10 }}>
          <div className="row gap-6">
            <TextInput label="From" type="date" value={from} onChange={(e) => { setFrom(e.target.value); setOffset(0); }} />
            <TextInput label="To" type="date" value={to} onChange={(e) => { setTo(e.target.value); setOffset(0); }} />
            <Select label="Status" value={status} onChange={(e) => { setStatus(e.target.value); setOffset(0); }}>
              <option value="">All</option>
              <option value="POSTED">Posted</option>
              <option value="VOID">Voided</option>
            </Select>
          </div>
          <div className="search" style={{ width: 240 }}>
            <input
              className="input"
              placeholder="Expense #, vendor, reference…"
              value={q}
              onChange={(e) => { setQ(e.target.value); setOffset(0); }}
            />
          </div>
          {can('expense:create') ? (
            <button className="btn primary" onClick={() => setRecording(true)}>
              + Add expense
            </button>
          ) : null}
        </div>

        {rows.length === 0 ? (
          <Empty icon="¤" message="No expenses match" />
        ) : (
          <div className="table-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Expense #</th>
                  <th>Date</th>
                  <th>Category</th>
                  <th className="num">Amount</th>
                  <th>Payment</th>
                  <th>Recorded by</th>
                  <th>Vendor</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id} className="clickable" onClick={() => setOpenId(row.id)}>
                    <td className="b mono">{row.number}</td>
                    <td className="nowrap">{date(row.expenseDate)}</td>
                    <td>{row.category ?? '—'}</td>
                    <td className="num b">{money(row.amount)}</td>
                    <td>{PAYMENT_MODE_LABELS[row.paymentMode]}</td>
                    <td>{row.createdBy ?? '—'}</td>
                    <td>{row.vendorName ?? '—'}</td>
                    <td>
                      <StatusChip status={row.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {total > limit ? (
          <div className="row between" style={{ padding: '10px 4px' }}>
            <span className="dim small">
              {offset + 1}–{Math.min(offset + limit, total)} of {total}
            </span>
            <div className="row gap-6">
              <button className="btn sm" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - limit))}>
                Previous
              </button>
              <button
                className="btn sm"
                disabled={offset + limit >= total}
                onClick={() => setOffset(offset + limit)}
              >
                Next
              </button>
            </div>
          </div>
        ) : null}
      </div>

      {recording ? <RecordExpenseModal onClose={() => setRecording(false)} /> : null}

      {openId ? (
        <Modal
          title={detail.data ? `Expense ${detail.data.number}` : 'Expense'}
          onClose={() => setOpenId(null)}
          footer={
            <>
              <button className="btn" onClick={() => setOpenId(null)}>
                Close
              </button>
              {can('expense:void') && detail.data?.status === 'POSTED' ? (
                <button className="btn danger" onClick={() => setVoiding(true)}>
                  Void expense
                </button>
              ) : null}
            </>
          }
        >
          {detail.isLoading || !detail.data ? (
            <Loading />
          ) : (
            <div className="col gap-10">
              <Row label="Category" value={detail.data.category ?? '—'} />
              <Row label="Amount" value={money(detail.data.amount)} />
              <Row label="Payment method" value={PAYMENT_MODE_LABELS[detail.data.paymentMode]} />
              <Row label="Expense date" value={date(detail.data.expenseDate)} />
              <Row label="Vendor / paid to" value={detail.data.vendorName ?? '—'} />
              <Row label="Reference number" value={detail.data.referenceNumber ?? '—'} />
              <Row label="Description" value={detail.data.description} />
              {detail.data.notes ? <Row label="Notes" value={detail.data.notes} /> : null}
              <Row label="Status" value={<StatusChip status={detail.data.status} />} />
              {detail.data.voidReason ? <Row label="Void reason" value={detail.data.voidReason} /> : null}
              <Row label="Recorded by" value={detail.data.createdBy ?? '—'} />
              <Row label="Recorded at" value={date(detail.data.createdAt)} />
            </div>
          )}
        </Modal>
      ) : null}

      {voiding ? (
        <Modal
          title="Void expense"
          size="narrow"
          onClose={() => setVoiding(false)}
          footer={
            <>
              <button className="btn" onClick={() => setVoiding(false)}>
                Cancel
              </button>
              <button
                className="btn danger"
                disabled={!voidReason.trim() || voidExpense.isPending}
                onClick={() => voidExpense.mutate()}
              >
                Void expense
              </button>
            </>
          }
        >
          <p className="dim" style={{ marginBottom: 14 }}>
            The expense is marked void and excluded from cash reconciliation from this point on.
            The original entry and who voided it stay on record.
          </p>
          <TextInput
            label="Reason"
            autoFocus
            value={voidReason}
            onChange={(e) => setVoidReason(e.target.value)}
          />
        </Modal>
      ) : null}
    </div>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="row between">
      <span className="dim">{label}</span>
      <span className="b">{value}</span>
    </div>
  );
}

/* ---------------- categories ---------------- */

function ExpenseCategoriesSection() {
  const queryClient = useQueryClient();
  const { push, fail } = useToast();
  const categories = useQuery({
    queryKey: ['expense-categories', 'all'],
    queryFn: () => api.get<ExpenseCategory[]>('/api/expense-categories'),
  });

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [department, setDepartment] = useState<ExpenseDepartment>('SHARED');

  const create = useMutation({
    mutationFn: () =>
      api.post('/api/expense-categories', {
        name: name.trim(),
        description: description.trim() || undefined,
        department,
      }),
    onSuccess: () => {
      push('Category added', 'ok');
      setName('');
      setDescription('');
      setDepartment('SHARED');
      void queryClient.invalidateQueries({ queryKey: ['expense-categories'] });
    },
    onError: fail,
  });

  const deactivate = useMutation({
    mutationFn: (id: string) => api.post(`/api/expense-categories/${id}/deactivate`),
    onSuccess: () => {
      push('Category deactivated', 'ok');
      void queryClient.invalidateQueries({ queryKey: ['expense-categories'] });
    },
    onError: fail,
  });

  const activate = useMutation({
    mutationFn: (id: string) => api.post(`/api/expense-categories/${id}/activate`),
    onSuccess: () => {
      push('Category activated', 'ok');
      void queryClient.invalidateQueries({ queryKey: ['expense-categories'] });
    },
    onError: fail,
  });

  const [editing, setEditing] = useState<ExpenseCategory | null>(null);

  if (categories.isLoading) return <Loading />;
  if (categories.error) return <ErrorState error={categories.error} onRetry={() => void categories.refetch()} />;

  return (
    <div className="stack">
      <div className="card">
        <div className="card-head">
          <h3>Add a category</h3>
        </div>
        <div className="card-body">
          <div className="form-grid">
            <TextInput label="Name *" value={name} onChange={(e) => setName(e.target.value)} />
            <TextInput label="Description" value={description} onChange={(e) => setDescription(e.target.value)} />
            <DepartmentSelect value={department} onChange={setDepartment} />
          </div>
          <button
            className="btn primary"
            style={{ marginTop: 10 }}
            disabled={!name.trim() || create.isPending}
            onClick={() => create.mutate()}
          >
            Add category
          </button>
        </div>
      </div>

      <div className="card">
        <div className="table-wrap">
          <table className="tbl">
            <thead>
              <tr>
                <th>Name</th>
                <th>Description</th>
                <th>Reported under</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {(categories.data ?? []).map((category) => (
                <tr key={category.id}>
                  <td className="b">{category.name}</td>
                  <td className="dim">{category.description ?? '—'}</td>
                  <td>{DEPARTMENT_OPTIONS.find(([id]) => id === category.department)?.[1] ?? '—'}</td>
                  <td>
                    <span className={`chip ${category.isActive ? 'ok' : 'danger'}`}>
                      {category.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>
                    <div className="row gap-6">
                      <button className="btn sm" onClick={() => setEditing(category)}>
                        Edit
                      </button>
                      {category.isActive ? (
                        <button
                          className="btn sm"
                          disabled={deactivate.isPending}
                          onClick={() => deactivate.mutate(category.id)}
                        >
                          Deactivate
                        </button>
                      ) : (
                        <button
                          className="btn sm primary"
                          disabled={activate.isPending}
                          onClick={() => activate.mutate(category.id)}
                        >
                          Activate
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {editing ? (
        <EditCategoryModal category={editing} onClose={() => setEditing(null)} />
      ) : null}
    </div>
  );
}

function EditCategoryModal({
  category,
  onClose,
}: {
  category: ExpenseCategory;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const { push, fail } = useToast();
  const [name, setName] = useState(category.name);
  const [description, setDescription] = useState(category.description ?? '');
  const [sortOrder, setSortOrder] = useState(String(category.sortOrder));
  const [department, setDepartment] = useState<ExpenseDepartment>(category.department);

  const save = useMutation({
    mutationFn: () =>
      api.put(`/api/expense-categories/${category.id}`, {
        name: name.trim(),
        description: description.trim() || undefined,
        sortOrder: Number(sortOrder) || 0,
        department,
      }),
    onSuccess: () => {
      push('Category updated', 'ok');
      void queryClient.invalidateQueries({ queryKey: ['expense-categories'] });
      onClose();
    },
    onError: fail,
  });

  return (
    <Modal
      title="Edit category"
      size="narrow"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn primary"
            disabled={!name.trim() || save.isPending}
            onClick={() => save.mutate()}
          >
            {save.isPending ? 'Saving…' : 'Save changes'}
          </button>
        </>
      }
    >
      <div className="col gap-14">
        <TextInput label="Name *" value={name} onChange={(e) => setName(e.target.value)} />
        <TextInput
          label="Description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
        <DepartmentSelect value={department} onChange={setDepartment} />
        <TextInput
          label="Sort order"
          type="number"
          value={sortOrder}
          onChange={(e) => setSortOrder(e.target.value)}
          hint="Lower numbers show first in the quick-pick list on the expense form."
        />
      </div>
    </Modal>
  );
}

/* ---------------- reports ---------------- */

function ExpenseReportsSection() {
  const [from, setFrom] = useState(daysAgo(6));
  const [to, setTo] = useState(today());

  const summary = useQuery({
    queryKey: ['expense-report-summary', from, to],
    queryFn: () =>
      api.get<{
        totalExpenses: number;
        cashExpenses: number;
        nonCashExpenses: number;
        count: number;
        byPaymentMode: Array<{ paymentMode: ExpensePaymentMode; amount: number; count: number }>;
        byCategory: Array<{ categoryId: string; category: string; amount: number; count: number }>;
        byCashier: Array<{ createdById: string; name: string; amount: number; count: number }>;
      }>(`/api/expenses/reports/summary?from=${from}&to=${to}`),
  });

  const daily = useQuery({
    queryKey: ['expense-daily-summary', to],
    queryFn: () =>
      api.get<{
        total: number;
        cash: CashSummary;
        byPaymentMode: Array<{ paymentMode: ExpensePaymentMode; amount: number }>;
      }>(`/api/expenses/reports/daily?date=${to}`),
  });

  return (
    <div className="stack">
      <div className="card">
        <div className="card-head">
          <div className="row gap-6">
            <TextInput label="From" type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
            <TextInput label="To" type="date" value={to} onChange={(e) => setTo(e.target.value)} />
          </div>
        </div>
        <div className="card-body">
          {summary.isLoading ? (
            <Loading />
          ) : summary.error ? (
            <ErrorState error={summary.error} onRetry={() => void summary.refetch()} />
          ) : (
            <>
              <div className="kpis">
                <Kpi label="Total expenses" value={money(summary.data!.totalExpenses)} foot={`${summary.data!.count} entries`} />
                <Kpi label="Cash" value={money(summary.data!.cashExpenses)} tone="amber" />
                <Kpi label="Non-cash" value={money(summary.data!.nonCashExpenses)} tone="blue" />
              </div>

              <div className="grid-2" style={{ marginTop: 18 }}>
                <div>
                  <h3>By category</h3>
                  <BarList
                    rows={summary.data!.byCategory.map((r) => ({ label: r.category, value: r.amount }))}
                    format={money}
                  />
                </div>
                <div>
                  <h3>By payment method</h3>
                  <BarList
                    rows={summary.data!.byPaymentMode.map((r) => ({
                      label: PAYMENT_MODE_LABELS[r.paymentMode],
                      value: r.amount,
                    }))}
                    format={money}
                  />
                </div>
              </div>

              <h3 style={{ marginTop: 18 }}>By cashier</h3>
              <div className="table-wrap">
                <table className="tbl">
                  <thead>
                    <tr>
                      <th>Cashier</th>
                      <th className="num">Expenses</th>
                      <th className="num">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {summary.data!.byCashier.map((row) => (
                      <tr key={row.createdById}>
                        <td>{row.name}</td>
                        <td className="num">{row.count}</td>
                        <td className="num b">{money(row.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>

      <div className="card">
        <div className="card-head">
          <h3>Cash reconciliation — {date(to)}</h3>
        </div>
        <div className="card-body">
          {daily.isLoading ? (
            <Loading />
          ) : daily.error ? (
            <ErrorState error={daily.error} onRetry={() => void daily.refetch()} />
          ) : (
            <div className="col gap-6">
              <Row label="Opening cash" value={money(daily.data!.cash.openingCash)} />
              <Row label="Cash sales" value={money(daily.data!.cash.cashSales)} />
              <Row label="Cash expenses" value={`- ${money(daily.data!.cash.cashExpenses)}`} />
              <Row label="Expected cash" value={money(daily.data!.cash.availableCash)} />
              <p className="dim small" style={{ marginTop: 6 }}>
                Opening cash plus today's cash sales, minus posted cash expenses. There is still no
                shift or closing count in this version, so this is not compared against a manually
                counted drawer at end of day - only the one opening figure is recorded (set from the
                Record Expense screen when there is cash in the drawer before the first sale).
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ---------------- page ---------------- */

export function ExpensesPage() {
  const { can } = useAuth();
  const [tab, setTab] = useState<'list' | 'categories' | 'reports'>('list');

  const tabs = [
    { id: 'list' as const, label: 'Expenses' },
    ...(can('expense:category:manage') ? [{ id: 'categories' as const, label: 'Categories' }] : []),
    ...(can('reports:view') ? [{ id: 'reports' as const, label: 'Reports' }] : []),
  ];

  return (
    <div className="stack">
      <PageHeader title="Expenses" subtitle="Petty cash and operational spending, recorded against today's cash drawer" />
      <Tabs tabs={tabs} active={tab} onChange={setTab} />
      {tab === 'list' ? (
        <ExpenseListSection />
      ) : tab === 'categories' ? (
        <ExpenseCategoriesSection />
      ) : (
        <ExpenseReportsSection />
      )}
    </div>
  );
}
